import React, { useState } from 'react';
import { Info, AlertCircle } from 'lucide-react';
import { Badge } from './ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { cn, getLevelColor } from '../lib/utils';

const LIMITROFES = new Set(['básico', 'basico', 'competente']);

/**
 * NivelEstimadoBadge — Badge de nivel con:
 *  - Popover informativo cuando el nivel es Básico o Competente (zona limítrofe).
 *  - Estado especial para "Información insuficiente" (no es una calificación).
 */
export default function NivelEstimadoBadge({ nivel, size = 'md', className, testid }) {
    const [open, setOpen] = useState(false);
    const nivelStr = String(nivel || '').trim();
    const lower = nivelStr.toLowerCase();
    const isInsufficient = lower === 'información insuficiente' || lower === 'informacion insuficiente';
    const isLimitrofe = LIMITROFES.has(lower);
    const sizeCls = size === 'sm' ? 'text-xs' : 'text-sm';

    // Estado especial: información insuficiente → no mostrar como calificación
    if (isInsufficient) {
        return (
            <div
                className={cn(
                    'inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-slate-100 px-2.5 py-1 font-medium text-slate-700',
                    sizeCls,
                    className,
                )}
                data-testid={testid || 'nivel-insufficient-badge'}
                title="Aún no hay evidencia suficiente para calificar este borrador"
            >
                <AlertCircle className="h-3.5 w-3.5 text-slate-500" />
                <span>Información insuficiente</span>
            </div>
        );
    }

    // Nivel normal (destacado / insuficiente / sin evaluar) → sin popover
    if (!isLimitrofe) {
        return (
            <Badge className={cn(sizeCls, getLevelColor(nivelStr), className)} data-testid={testid}>
                {nivelStr || '—'}
            </Badge>
        );
    }

    // Básico / Competente → popover informativo (zona limítrofe)
    return (
        <span className={cn('inline-flex items-center gap-1', className)} data-testid={testid || 'nivel-limitrofe-wrap'}>
            <Badge className={cn(sizeCls, getLevelColor(nivelStr))}>{nivelStr}</Badge>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex h-5 w-5 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition-colors"
                        aria-label="¿Por qué puede ocurrir esto?"
                        data-testid="nivel-limitrofe-info-btn"
                    >
                        <Info className="h-3.5 w-3.5" />
                    </button>
                </PopoverTrigger>
                <PopoverContent
                    align="start"
                    side="bottom"
                    className="w-[380px] max-w-[92vw] p-0 border border-amber-200 shadow-lg"
                    data-testid="nivel-limitrofe-popover"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="rounded-t-md bg-gradient-to-br from-amber-50 to-white px-4 py-3 border-b border-amber-100">
                        <div className="flex items-center gap-2">
                            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-amber-100 text-amber-800">
                                <Info className="h-4 w-4" />
                            </div>
                            <h4 className="font-heading text-sm font-bold text-amber-900">
                                Análisis en zona limítrofe
                            </h4>
                        </div>
                    </div>
                    <div className="px-4 py-3 text-xs text-slate-700 space-y-2.5 leading-relaxed">
                        <p>
                            Este borrador presenta características que lo sitúan muy cerca del límite entre los niveles
                            <strong> Básico y Competente</strong>. En este tipo de casos, pequeñas mejoras en la claridad del
                            objetivo, la precisión conceptual, la coherencia entre actividades y evaluación, o la
                            explicitación de criterios de logro, pueden modificar la valoración final.
                        </p>
                        <p>
                            Si al volver a analizar este mismo borrador obtienes una calificación distinta entre
                            <strong> Básico y Competente</strong>, te recomendamos:
                        </p>
                        <ul className="space-y-1.5 pl-1">
                            <li className="flex items-start gap-1.5"><span className="text-emerald-600">✅</span><span>Anotar en tu cuaderno de trabajo <strong>ambas calificaciones</strong> obtenidas.</span></li>
                            <li className="flex items-start gap-1.5"><span className="text-emerald-600">✅</span><span>Guardar este borrador <strong>sin modificarlo</strong>.</span></li>
                            <li className="flex items-start gap-1.5"><span className="text-emerald-600">✅</span><span>Llevar este caso a la próxima <strong>capacitación semanal</strong> y compartirlo con la <strong>Docente Experta I</strong>, quien analizará contigo los aspectos que están marcando la diferencia y te orientará sobre las mejoras más convenientes.</span></li>
                        </ul>
                        <p className="text-[11px] italic text-slate-500 border-t border-slate-100 pt-2 mt-2">
                            Recuerda que Portafolio Digital Docente combina el apoyo de Inteligencia Artificial con la
                            orientación de una Docente Experta I, porque los casos limítrofes requieren un análisis
                            pedagógico más profundo que fortalece tu proceso de aprendizaje profesional.
                        </p>
                    </div>
                </PopoverContent>
            </Popover>
        </span>
    );
}
